/**
 * MarkdownText — minimal inline markdown for the agent's chat replies.
 *
 * Claude writes `**bold**` (and occasionally `` `code` ``), but the chat was showing
 * the literal markers. This renders **bold** / __bold__ as <strong> and `code` as
 * <code>, and STRIPS any stray `**`/`__` so an asterisk never shows — even when the
 * agent drops a closing marker. Matching is kept to a single line (bold/code don't
 * span paragraphs), which avoids mis-pairing across an unclosed marker.
 *
 * Line breaks are preserved by the parent's `whitespace-pre-wrap`.
 */
import type { ReactNode } from 'react';

const PATTERNS: Array<{ re: RegExp; el: (inner: string, key: string) => ReactNode }> = [
  { re: /\*\*([^\n]+?)\*\*/, el: (s, k) => <strong key={k}>{s}</strong> },
  { re: /__([^\n]+?)__/, el: (s, k) => <strong key={k}>{s}</strong> },
  { re: /`([^`\n]+?)`/, el: (s, k) => <code key={k} className="px-1 py-0.5 rounded bg-surface-light/70 text-[0.85em] font-mono">{s}</code> },
];

// Remove leftover/unclosed bold markers so they never render literally.
const stripStray = (s: string) => s.replace(/\*\*/g, '').replace(/__/g, '');

function tokenize(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let rest = text;
  let n = 0;
  while (rest.length) {
    let best: { idx: number; full: number; node: ReactNode } | null = null;
    for (const p of PATTERNS) {
      const m = p.re.exec(rest);
      if (m && m[1].trim() && (best === null || m.index < best.idx)) {
        best = { idx: m.index, full: m[0].length, node: p.el(m[1], `md-${n}`) };
      }
    }
    if (!best) { nodes.push(stripStray(rest)); break; }
    if (best.idx > 0) nodes.push(stripStray(rest.slice(0, best.idx)));
    nodes.push(best.node);
    rest = rest.slice(best.idx + best.full);
    n++;
  }
  return nodes;
}

export function MarkdownText({ text }: { text: string }) {
  return <>{tokenize(text)}</>;
}
